import { doc, runTransaction, Timestamp } from 'firebase/firestore'
import { db } from './firebase.config'
import { getCollection } from './firestore.service'

const COUNTERS_COLLECTION = 'counters'

const toAsciiUpper = (value) => {
  const raw = typeof value === 'string' || typeof value === 'number' ? String(value) : ''
  const normalized = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  return normalized.toUpperCase()
}

const toAlphaNumUpper = (value) => toAsciiUpper(value).replace(/[^A-Z0-9]+/g, '')

const takePrefix = (value, count) => {
  const n = Number(count)
  const size = Number.isFinite(n) ? Math.max(1, Math.min(30, Math.round(n))) : 3
  const normalized = toAlphaNumUpper(value)
  return normalized.slice(0, size)
}

const hashString = (input) => {
  const str = typeof input === 'string' ? input : JSON.stringify(input ?? '')
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i)
    hash = hash >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}

const getProductColorToken = (product) => {
  const colorNames = product?.colorNames && typeof product.colorNames === 'object' ? product.colorNames : {}
  const rawHex = typeof product?.color === 'string' && product.color.startsWith('#') ? product.color : null
  const label = rawHex ? (colorNames[rawHex] || product?.colorName || '') : (product?.colorName || '')
  if (label) return label
  if (rawHex) return rawHex.replace('#', '')
  if (typeof product?.color === 'string' && product.color.trim()) return product.color.trim()
  return ''
}

const getProductSizeToken = (product) => {
  const sizes = Array.isArray(product?.sizes) ? product.sizes.filter(s => typeof s === 'string' && s.trim()) : []
  if (sizes.length === 1) return sizes[0].trim()
  return ''
}

export const getNextCounterValue = async (counterId) => {
  const id = String(counterId || '').trim()
  if (!id) throw new Error('CounterId inválido')

  return await runTransaction(db, async (tx) => {
    const ref = doc(db, COUNTERS_COLLECTION, id)
    const snap = await tx.get(ref)
    const now = Timestamp.now()
    const current = snap.exists() ? Number(snap.data()?.value || 0) : 0
    const next = (Number.isFinite(current) ? current : 0) + 1
    const payload = snap.exists()
      ? { value: next, updatedAt: now }
      : { value: next, createdAt: now, updatedAt: now }
    tx.set(ref, payload, { merge: true })
    return next
  })
}

export const generateSerialNumber = async (digits) => {
  const raw = Number(digits)
  const size = Number.isFinite(raw) ? Math.max(4, Math.min(30, Math.round(raw))) : 15

  for (let attempt = 0; attempt < 5; attempt++) {
    const next = await getNextCounterValue('serialNumber')
    const serial = String(next).padStart(size, '0')
    const existing = await getCollection('products', [{ field: 'serialNumber', operator: '==', value: serial }], null, 'asc', 1)
    if (!existing.length) return serial
  }

  throw new Error('No se pudo generar un número de serie único')
}

export const generateSkuForProduct = async (product, rules) => {
  const skuAuto = rules?.productIdentification?.skuAuto || {}
  const enabled = skuAuto?.enabled === true
  if (!enabled) return { sku: '', meta: null }

  const separator = typeof skuAuto.separator === 'string' ? skuAuto.separator : '-'
  const rawParts = Array.isArray(skuAuto.parts) ? skuAuto.parts : []
  const parts = rawParts.filter(Boolean)

  const hasCounter = parts.some(p => p?.field === 'counter')
  const normalizedParts = hasCounter ? parts : [...parts, { field: 'counter', take: 4 }]

  const materialized = []
  for (const p of normalizedParts) {
    const field = p?.field
    const take = p?.take
    if (field === 'counter') {
      const next = await getNextCounterValue('sku')
      const digits = Number.isFinite(Number(take)) ? Math.max(1, Math.min(30, Math.round(Number(take)))) : 4
      materialized.push(String(next).padStart(digits, '0'))
      continue
    }
    if (field === 'name') materialized.push(takePrefix(product?.name, take))
    else if (field === 'brand') materialized.push(takePrefix(product?.brand, take))
    else if (field === 'collection') materialized.push(takePrefix(product?.collection, take))
    else if (field === 'color') materialized.push(takePrefix(getProductColorToken(product), take))
    else if (field === 'size') materialized.push(takePrefix(getProductSizeToken(product), take))
  }

  const sku = materialized.filter(Boolean).join(separator).replace(new RegExp(`${separator}{2,}`, 'g'), separator)
  if (!sku) throw new Error('No se pudo generar SKU con la configuración actual')

  const existing = await getCollection('products', [{ field: 'sku', operator: '==', value: sku }], null, 'asc', 1)
  if (existing.length && existing[0]?.id !== product?.id) {
    throw new Error('El SKU autogenerado ya existe. Ajustá la configuración (por ejemplo, agregando autonumérico).')
  }

  const configHash = hashString({ separator, parts: normalizedParts })
  return {
    sku,
    meta: { autoGenerated: true, generatedAt: new Date(), configHash }
  }
}
